import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNextStep, useStepParams } from '../../app/navigation';
import { Screen } from '../../chrome';
import {
  Button,
  CarrierLogo,
  Flap,
  Hint,
  Note,
  Question,
  Spinner,
  TextField,
  TextLink,
} from '../../components';
import { useLocale, useT } from '../../i18n';
import { useTrack } from '../../lib/analytics';
import {
  queryKeys,
  useApi,
  useChooseLeg,
  useLegFlights,
  type Coverage,
  type Enquiry,
  type Flight,
  isTurnstileError,
} from '../../lib/api';
import { useFeatureValue } from '../../lib/flags';
import { formatDate } from '../../lib/format';
import { useTrip } from '../../lib/funnel';
import S3Manual from '../S3-manual';
import { EMPTY_MANUAL, type ManualValue } from '../S3-manual/manual';
import { StageError, TripSummary } from '../S1/stage';
import { useStage } from '../S1/trip';
import { FlightDataCredit } from './Credit';
import { addDays, delayCaption, flapOf, hhmm, matchesFilter } from './flight';
import { TrustPills } from './TrustPills';
import './S3.css';

type Chip = 'delayed' | 'cancelled' | 'all';
const CHIPS: Chip[] = ['delayed', 'cancelled', 'all'];
/** Coverage with a list to pick from (Variant A with chips, Variant B without). */
const LISTED: Coverage[] = ['full', 'schedule_only'];
/** No data to check a typed number against: "Continue anyway" without a "couldn't find". */
const UNCHECKABLE: Coverage[] = ['beyond_horizon', 'unavailable'];
const LOOKUP_STALE_MS = 10 * 60 * 1000;

/**
 * S3 · Find your flight — one leg per render (`?leg=1` for a connecting flight; the resolver
 * picks the leg). The first search creates the enquiry (architecture.md §6). S3-manual is
 * revealed in place.
 */
export default function ScreenS3() {
  const { leg } = useStepParams();
  // A new leg starts clean: its own list, filter, chip and manual entry.
  return <S3Leg key={leg} leg={leg} />;
}

function S3Leg({ leg }: { leg: number }) {
  const t = useT();
  const locale = useLocale();
  const track = useTrack();
  const api = useApi();
  const qc = useQueryClient();
  const { goNext } = useNextStep();
  const { enquiryId, enquiry, trip, error: loadError } = useStage('S3', leg);
  const { ensureEnquiry, entry } = useTrip();
  const choose = useChooseLeg(enquiryId);
  const flagChip = useFeatureValue('s3_default_chip');

  // The enquiry is created at the first flight search, with the trip (once, StrictMode or not).
  const creating = useRef(false);
  const [createError, setCreateError] = useState<unknown>(null);
  const tripComplete = !!(trip.origin && trip.destination && trip.date);
  useEffect(() => {
    if (enquiryId || !tripComplete || creating.current) return;
    creating.current = true;
    ensureEnquiry().catch(setCreateError);
  }, [enquiryId, tripComplete, ensureEnquiry]);

  const legData = enquiry?.legs[leg];
  const legs = enquiry?.legs.length ?? trip.connections.length + 1;
  const tripDate = enquiry?.trip.date ?? trip.date;
  const [nextDay, setNextDay] = useState(() => !!legData && !!tripDate && legData.date !== tripDate);
  /* A later leg always names its date (the trip date or the day after): the API's default is
     the leg's stored date, which may still be the other one (contract 1.1, LegDate). */
  const legDate = leg > 0 && tripDate ? (nextDay ? addDays(tripDate, 1) : tripDate) : undefined;
  const flights = useLegFlights(enquiry ? enquiryId : null, leg, legDate);
  const coverage = flights.data?.coverage;
  const all = flights.data?.flights ?? [];
  const hasList = !!coverage && LISTED.includes(coverage) && all.length > 0;
  const chipsOn = coverage === 'full';

  const [q, setQ] = useState('');
  const [chip, setChip] = useState<Chip>(
    entry === 'cancelled' ? 'cancelled' : entry === 'delayed' ? 'delayed' : flagChip,
  );
  const [picked, setPicked] = useState<string | null>(null);
  const chosen = picked ?? (legData?.choice === 'listed' ? (legData.flight?.id ?? null) : null);

  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState<ManualValue>(EMPTY_MANUAL);
  const [notFound, setNotFound] = useState<string | null>(null);
  /* A failed save: the message key (the bot check has its own, w5-11). */
  const [saveError, setSaveError] = useState<'common.turnstileFailed' | 'S3.saveFailed' | null>(null);
  const [busy, setBusy] = useState(false);
  const manualShown = manualOpen || (!!flights.data && !hasList) || flights.isError;

  const reported = useRef(false);
  useEffect(() => {
    if (!flights.data || reported.current) return;
    reported.current = true;
    track('s3_results', { leg, coverage: flights.data.coverage, count: flights.data.flights.length });
  }, [flights.data, leg, track]);

  const pool = all.filter((f) => matchesFilter(f, q));
  const rows = !chipsOn || chip === 'all' ? pool : pool.filter((f) => f.displayStatus === chip);
  const hidden = pool.length - rows.length;
  const count = (c: Chip) => (c === 'all' ? pool.length : pool.filter((f) => f.displayStatus === c).length);

  async function run(action: () => Promise<Enquiry | null>) {
    setBusy(true);
    setSaveError(null);
    try {
      const e = await action();
      if (e) await goNext(e);
    } catch (e) {
      setSaveError(isTurnstileError(e) ? 'common.turnstileFailed' : 'S3.saveFailed');
    } finally {
      setBusy(false);
    }
  }

  const onContinue = () =>
    run(async () => {
      if (!enquiryId) return null;
      if (!manualShown) {
        if (!chosen) return null;
        const f = all.find((x) => x.id === chosen);
        track('s3_flight_chosen', { leg, choice: 'listed', coverage, status: f?.displayStatus });
        return choose.mutateAsync({ leg, choice: 'listed', flightId: chosen });
      }
      const { airline, number } = manual;
      if (!airline || !number) return null;
      const ident = `${airline.iata}${number}`;
      if (!notFound) {
        const found = await qc.fetchQuery({
          queryKey: queryKeys.flightLookup(enquiryId, leg, ident, legDate),
          queryFn: ({ signal }) =>
            api.lookupLegFlight(
              enquiryId,
              leg,
              { carrier: airline.iata, number, ...(legDate ? { date: legDate } : {}) },
              { signal },
            ),
          staleTime: LOOKUP_STALE_MS,
        });
        track('s3_manual_lookup', { leg, found: !!found.flight, coverage: found.coverage });
        if (!found.flight && !UNCHECKABLE.includes(found.coverage)) {
          setNotFound(t('S3-manual.notFound', { ident, date: formatDate(found.date) }));
          return null;
        }
      }
      track('s3_flight_chosen', { leg, choice: 'manual', found: !notFound });
      return choose.mutateAsync({
        leg,
        choice: 'manual',
        carrier: airline.iata,
        number,
        ...(legDate ? { date: legDate } : {}),
      });
    });

  const onSkip = () =>
    run(() => {
      track('s3_leg_skipped', { leg });
      return choose.mutateAsync({ leg, choice: 'skipped' });
    });

  const from = legData?.origin ?? trip.origin?.iata ?? '—';
  const to = legData?.destination ?? trip.destination?.iata ?? '—';
  const heading =
    legs < 2
      ? t('S3.title')
      : leg === 0
        ? t('S3.titleFirst', { from, to })
        : t('S3.titleConnecting', { from, to });

  const ready = manualShown ? !!(manual.airline && manual.number) : !!chosen;
  const searching = !flights.data && !flights.isError && !loadError && !createError;

  return (
    <Screen
      id="S3"
      title={t('S3.title')}
      dock={
        <Button onClick={onContinue} disabled={!ready || busy}>
          {manualShown && notFound ? t('S3-manual.continueAnyway') : t('common.continue')}
        </Button>
      }
    >
      {loadError || createError ? (
        <StageError />
      ) : (
        <>
          <TripSummary enquiry={enquiry} trip={trip} editTo="S1" />
          <Question small>{heading}</Question>

          {searching && (
            <div className="s3-searching" role="status">
              <Spinner />
              <span>{t('S3.searching')}</span>
              <TrustPills />
            </div>
          )}

          {hasList && !manualOpen && (
            <>
              <TextField
                aria-label={t('S3.filterLabel')}
                placeholder={t('S3.filterPlaceholder')}
                autoComplete="off"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              {chipsOn && (
                <div className="chips" role="group" aria-label={t('S3.chipsLabel')}>
                  {CHIPS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={`chip${chip === c ? ' is-on' : ''}`}
                      aria-pressed={chip === c}
                      onClick={() => {
                        setChip(c);
                        track('s3_chip', { chip: c });
                      }}
                    >
                      {t(`S3.chip.${c}`)}
                      <span className="chip__n">{count(c)}</span>
                    </button>
                  ))}
                </div>
              )}
              {rows.length ? (
                <div className="frows">
                  {rows.map((f) => (
                    <FlightRow
                      key={f.id}
                      flight={f}
                      withStatus={chipsOn}
                      on={f.id === chosen}
                      onPick={() => setPicked(f.id)}
                    />
                  ))}
                </div>
              ) : (
                <div className="frow__empty">
                  {chip === 'all'
                    ? t('S3.emptyFiltered')
                    : t('S3.emptyChip', { chip: t(`S3.chip.${chip}`).toLocaleLowerCase(locale) })}
                  <br />
                  {t('S3.emptyFilteredHint')}
                </div>
              )}
              {hidden > 0 && (
                <TextLink quiet onClick={() => setChip('all')}>
                  {t('S3.showAll', { count: hidden })}
                </TextLink>
              )}
              <TextLink
                onClick={() => {
                  setManualOpen(true);
                  track('s3_manual_opened', { leg });
                }}
              >
                {t('S3.cantFind')}
              </TextLink>
            </>
          )}

          {flights.data && !hasList && (
            <div className="frow__empty">{t(emptyKey(flights.data.coverage))}</div>
          )}
          {flights.isError && (
            <div className="frow__empty">
              {t(isTurnstileError(flights.error) ? 'common.turnstileFailed' : 'S3.searchFailed')}{' '}
              <TextLink quiet onClick={() => void flights.refetch()}>
                {t('S3.tryAgain')}
              </TextLink>
            </div>
          )}

          {manualShown && (
            <S3Manual
              value={manual}
              onChange={(v) => {
                setManual(v);
                setNotFound(null);
              }}
              message={notFound}
              onBackToList={hasList ? () => setManualOpen(false) : undefined}
              autoFocus={manualOpen}
            />
          )}

          {saveError && <Hint warn>{t(saveError)}</Hint>}

          {leg > 0 && tripDate && (
            <>
              <Note>
                {nextDay
                  ? t('S3.nextDayOn', { date: formatDate(addDays(tripDate, 1)) })
                  : t('S3.sameDayOn', { date: formatDate(tripDate) })}{' '}
                <button
                  type="button"
                  className="inlink"
                  onClick={() => {
                    setNextDay((d) => !d);
                    setPicked(null);
                    setNotFound(null); // it was about the other day
                    track('s3_next_day', { leg, on: !nextDay });
                  }}
                >
                  {nextDay ? t('S3.sameDay') : t('S3.nextDay')}
                </button>
              </Note>
              <TextLink quiet onClick={onSkip} disabled={busy}>
                {t('S3.skip')}
              </TextLink>
            </>
          )}
        </>
      )}
    </Screen>
  );
}

function emptyKey(c: Coverage) {
  if (c === 'beyond_horizon') return 'S3.emptyBeyondHorizon' as const;
  if (c === 'unavailable') return 'S3.emptyUnavailable' as const;
  return 'S3.empty' as const;
}

/** One operating flight: ident + carrier, times, "also sold as …", and (Variant A) its status. */
function FlightRow({
  flight: f,
  withStatus,
  on,
  onPick,
}: {
  flight: Flight;
  withStatus: boolean;
  on: boolean;
  onPick: () => void;
}) {
  const t = useT();
  const flap = flapOf(f);
  const caption = withStatus ? delayCaption(t, f) : '';
  return (
    <button type="button" className={`frow${on ? ' is-on' : ''}`} aria-pressed={on} onClick={onPick}>
      <span className="frow__main">
        <span className="frow__id">
          {f.operatingIdent}
          <span className="frow__carrier">
            <CarrierLogo src={f.operator.logoUrl} />
            {f.operator.name}
          </span>
        </span>
        <span className="frow__times">
          {hhmm(f.scheduledOutLocal)}
          <span aria-hidden="true">✈</span>
          {hhmm(f.scheduledInLocal)}
        </span>
        {f.marketingIdents.length > 0 && (
          <span className="frow__codeshare">
            {t('S3.codeshare', { idents: f.marketingIdents.join(', ') })}
          </span>
        )}
      </span>
      {withStatus && (
        <span className="frow__status">
          <Flap text={t(flap.key)} tone={flap.tone} />
          {caption && <span className="frow__delay">{caption}</span>}
          {caption && <FlightDataCredit flight={f} className="frow__credit" />}
        </span>
      )}
    </button>
  );
}
